// A scoring scale is the set of values a checklist heading is rated on, and the
// colour each value is drawn in. It is a property of the *heading*, so two
// headings on the same checklist can be scored on different ranges — 1-5 for a
// POMMe-Health heading, 0-4 for an ICF one, 1-10 for a ten-point one.
//
// Everything that renders or accepts a score reads the range from here rather
// than assuming 1-5. See docs/scoring-scales.md.

// The scale a heading falls back to when it names none. The API applies the
// same fallback on its DTO paths, but the checklist entity endpoints
// (GET /checklists/patient/{id}) serialize the raw row, where a heading that
// names no scale comes back with `scoringScale: null` — so the fallback has to
// exist on this side too. The values match the seeded POMMe-Health scale.
export const DEFAULT_SCORING_SCALE = {
  id: null,
  name: "POMMe-Health",
  description: "POMMe-Health 1-5 scale",
  systemDefined: true,
  defaultScale: true,
  organizationId: null,
  active: true,
  values: [
    { value: 1, label: null, color: "#ff0000", orderIndex: 0 },
    { value: 2, label: null, color: "#ffa500", orderIndex: 1 },
    { value: 3, label: null, color: "#ffff00", orderIndex: 2 },
    { value: 4, label: null, color: "#008000", orderIndex: 3 },
    { value: 5, label: null, color: "#0000ff", orderIndex: 4 },
  ],
};

export const DEFAULT_SCORING_SCALE_NAME = DEFAULT_SCORING_SCALE.name;

// What an explicit "not applicable" answer is labelled and picked as. It lives
// here rather than in scoreState so the pill helpers below can recognise it
// without importing back from a module that imports this one.
export const NOT_APPLICABLE_LABEL = "N/A";

// The hex the API ships the built-in scales in, mapped onto the brand colours
// the portal already paints scores with. Painting #ff0000 directly would repaint
// every existing checklist a different red, so a known colour keeps its class
// and only a colour outside this set falls through to an inline swatch.
const BRAND_CLASS_BY_HEX = {
  "#ff0000": "bg_danger",
  "#ffa500": "bg_orange",
  "#ffff00": "bg_yellow",
  "#008000": "bg_green",
  "#0000ff": "bg_blue",
};

// What those classes actually paint, for the PDF export — which cannot use CSS.
const BRAND_RGB_BY_CLASS = {
  bg_danger: [254, 68, 68],
  bg_orange: [255, 184, 0],
  bg_yellow: [254, 255, 0],
  bg_green: [135, 202, 69],
  bg_blue: [78, 135, 197],
};

const normalizeHex = (color) => {
  if (typeof color !== "string") return null;
  const trimmed = color.trim().toLowerCase();
  if (!trimmed) return null;
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  // Expand #abc to #aabbcc so short and long forms compare equal.
  if (/^#[0-9a-f]{3}$/.test(withHash)) {
    return `#${withHash[1]}${withHash[1]}${withHash[2]}${withHash[2]}${withHash[3]}${withHash[3]}`;
  }
  return /^#[0-9a-f]{6}$/.test(withHash) ? withHash : null;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  return [
    parseInt(normalized.slice(1, 3), 16),
    parseInt(normalized.slice(3, 5), 16),
    parseInt(normalized.slice(5, 7), 16),
  ];
};

// Black on a light swatch, white on a dark one, so a custom colour stays
// readable whatever the organization picked.
const readableTextColor = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#212529" : "#ffffff";
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// A scale in a predictable shape: values present, numeric, and in display
// order. Anything falsy — a heading with no scale, a scale whose values never
// came back — resolves to the default scale rather than to an empty picker.
export const normalizeScoringScale = (scale) => {
  const values = Array.isArray(scale?.values) ? scale.values : [];
  const usable = values
    .map((entry) => ({
      ...entry,
      value: toNumber(entry?.value),
    }))
    .filter((entry) => entry.value !== null)
    .sort((a, b) => {
      const aOrder = toNumber(a.orderIndex);
      const bOrder = toNumber(b.orderIndex);
      if (aOrder !== null && bOrder !== null && aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return a.value - b.value;
    });

  if (!usable.length) return DEFAULT_SCORING_SCALE;
  return { ...scale, values: usable };
};

// The numbers a score may take on this scale, in display order. Render pickers
// and score columns from this — never from a range: the values are not
// guaranteed to be contiguous or to start at 1.
export const getScaleValues = (scale) =>
  normalizeScoringScale(scale).values.map((entry) => entry.value);

export const getScaleName = (scale) =>
  normalizeScoringScale(scale)?.name || DEFAULT_SCORING_SCALE_NAME;

export const isDefaultScoringScale = (scale) =>
  getScaleName(scale) === DEFAULT_SCORING_SCALE_NAME;

const findScaleValue = (scale, value) => {
  const target = toNumber(value);
  if (target === null) return null;
  return (
    normalizeScoringScale(scale).values.find(
      (entry) => entry.value === target
    ) || null
  );
};

export const isValidScaleScore = (scale, value) =>
  findScaleValue(scale, value) !== null;

// Optional wording for a point ("No impairment"). The built-in scales ship
// without labels, so this is usually null.
export const getScaleValueLabel = (scale, value) =>
  findScaleValue(scale, value)?.label || null;

export const getScaleValueColor = (scale, value) =>
  normalizeHex(findScaleValue(scale, value)?.color);

// How to paint the pill/circle for one score: the brand class when the scale
// uses a colour the portal already has one for, an inline swatch otherwise.
// `fallbackClass` covers anything off the scale (N/A, unanswered).
export const getScorePillProps = (scale, value, fallbackClass = "") => {
  const color = getScaleValueColor(scale, value);
  if (!color) return { className: fallbackClass, style: undefined };

  const brandClass = BRAND_CLASS_BY_HEX[color];
  if (brandClass) return { className: brandClass, style: undefined };

  return {
    className: "",
    style: { backgroundColor: color, color: readableTextColor(color) },
  };
};

// The same colour as RGB, for the PDF export. Known colours resolve through the
// brand palette so an exported POMMe-Health checklist looks like the screen.
export const getScoreRgb = (scale, value) => {
  const color = getScaleValueColor(scale, value);
  if (!color) return null;
  const brandClass = BRAND_CLASS_BY_HEX[color];
  if (brandClass) return BRAND_RGB_BY_CLASS[brandClass];
  return hexToRgb(color);
};

// Which scale a given subheading is scored on, from a list of headings in any
// of the shapes the API returns them (checklist headings, score-response
// headings). Score payloads are flat lists of subheadings drawn from several
// headings at once, so they need this to find the right range per row.
export const buildSubheadingScaleMap = (headings = []) => {
  const map = new Map();
  for (const heading of headings || []) {
    const scale = heading?.scoringScale;
    for (const subheading of heading?.subheadings || []) {
      const id = subheading?.id ?? subheading?.subheadingId;
      if (id !== undefined && id !== null) map.set(String(id), scale);
    }
  }
  return map;
};

// A resolver in the shape toScoresPayload wants, from that map.
export const scaleResolverFor = (headings = []) => {
  const map = buildSubheadingScaleMap(headings);
  return (subheadingId) => map.get(String(subheadingId));
};

// The pill for one option in a score picker: a value from the scale, or the
// explicit N/A that sits alongside them and reads as "no score".
export const getScoreOptionPillProps = (
  scale,
  value,
  naClass = "head_light"
) =>
  value === NOT_APPLICABLE_LABEL
    ? { className: naClass, style: undefined }
    : getScorePillProps(scale, value);
